import { useEffect, useState } from 'react';

interface ResponsiveSize {
  isTablet: boolean;
  isDesktop: boolean;
  isTabletLandscape: boolean;
}

const BREAKPOINT_TABLET = '(min-width: 768px)';
const BREAKPOINT_DESKTOP = '(min-width: 1025px)';
const ORIENTATION_LANDSCAPE = '(orientation: landscape)';

/**
 * Custom hook for responsive design checks
 * Automatically updates on window resize and browser dev tools device emulation
 * Eliminates need to use magic numbers or window.innerWidth checks
 *
 * @returns ResponsiveSize object with boolean flags for each breakpoint
 *
 * @example
 * const { isDesktop, isTablet, isTabletLandscape } = useResponsiveSize();
 * return isDesktop ? <DesktopLayout /> : <MobileLayout />;
 */
export const useResponsiveSize = (): ResponsiveSize => {
  const [size, setSize] = useState<ResponsiveSize>(() => {
    const tabletMediaQuery = window.matchMedia(BREAKPOINT_TABLET);
    const desktopMediaQuery = window.matchMedia(BREAKPOINT_DESKTOP);
    const landscapeMediaQuery = window.matchMedia(ORIENTATION_LANDSCAPE);

    const isTablet = tabletMediaQuery.matches;
    const isDesktop = desktopMediaQuery.matches;
    const isTabletLandscape = isTablet && !isDesktop && landscapeMediaQuery.matches;

    return {
      isTablet,
      isDesktop,
      isTabletLandscape,
    };
  });

  useEffect(() => {
    const tabletMediaQuery = window.matchMedia(BREAKPOINT_TABLET);
    const desktopMediaQuery = window.matchMedia(BREAKPOINT_DESKTOP);
    const landscapeMediaQuery = window.matchMedia(ORIENTATION_LANDSCAPE);

    const handleChange = (): void => {
      const isTablet = tabletMediaQuery.matches;
      const isDesktop = desktopMediaQuery.matches;
      const isTabletLandscape = isTablet && !isDesktop && landscapeMediaQuery.matches;

      setSize({
        isTablet,
        isDesktop,
        isTabletLandscape,
      });
    };

    // Set initial state
    handleChange();

    // Listen for media query changes
    tabletMediaQuery.addEventListener('change', handleChange);
    desktopMediaQuery.addEventListener('change', handleChange);
    landscapeMediaQuery.addEventListener('change', handleChange);

    return (): void => {
      tabletMediaQuery.removeEventListener('change', handleChange);
      desktopMediaQuery.removeEventListener('change', handleChange);
      landscapeMediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return size;
};
